import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import CaptchaWidget from "./CaptchaWidget";
import { api } from "../../lib/api";

const USE_CASES = [
  { value: "sast", label: "SAST — source code scanning" },
  { value: "dast", label: "DAST — running app scanning" },
  { value: "sca", label: "SCA — dependency / CVE" },
  { value: "compliance", label: "Compliance / audit evidence" },
  { value: "all", label: "All of the above" },
];

const EMPTY = {
  name: "",
  email: "",
  company: "",
  target_url: "",
  use_case: "",
  note: "",
};

/**
 * Talk-to-Security / Design-Partner intake dialog.
 * Wraps any trigger element; POSTs to /api/v1/design-partner-interest.
 *
 * Props:
 *  - trigger: React node (button, link, etc.) used as DialogTrigger child
 *  - source:  string identifier for analytics / testid suffix (e.g. "landing-hero")
 */
export default function DesignPartnerDialog({ trigger, source = "default" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      // reset slightly after close animation
      setTimeout(() => {
        setForm(EMPTY);
        setSubmitted(false);
      }, 250);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/design-partner-interest", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        target_url: form.target_url.trim() || null,
        use_case: form.use_case || null,
        note: form.note.trim() || null,
        captcha_token: captchaToken,
      });
      setSubmitted(true);
      toast.success("Got it. Security will reach out within one business day.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error(
          typeof detail === "string"
            ? detail
            : "You're moving fast — please try again in a minute.",
        );
      } else if (status === 422) {
        toast.error("Please check the form — that email doesn't look right.");
      } else {
        toast.error("Couldn't send your note. Try again, or email security@argus.security.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild data-testid={`design-partner-trigger-${source}`}>
        {trigger}
      </DialogTrigger>
      <DialogContent
        className="bg-panel border-line text-ink sm:max-w-lg"
        data-testid="design-partner-dialog"
      >
        {submitted ? (
          <div className="py-4" data-testid="design-partner-success">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl tracking-tight">
                Thank you.
              </DialogTitle>
              <DialogDescription className="text-ink/70 text-base mt-2">
                Security will reach out within one business day from{" "}
                <span className="font-mono text-gold">security@argus.security</span>.
                Watch your spam folder — we sign every message.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6">
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-gold text-paper hover:bg-gold2 font-semibold"
                data-testid="design-partner-close"
              >
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="design-partner-form">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl tracking-tight">
                Talk to Security
              </DialogTitle>
              <DialogDescription className="text-ink/70 mt-1">
                Tell us what you'd like ArgusSecure to scan. No sales gauntlet — a real
                security engineer replies within one business day.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 mt-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="dp-name" className="text-xs uppercase tracking-wider text-ink/60">
                    Name <span className="text-gold">*</span>
                  </Label>
                  <Input
                    id="dp-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                    autoComplete="name"
                    className="bg-paper border-line text-ink"
                    data-testid="design-partner-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dp-email" className="text-xs uppercase tracking-wider text-ink/60">
                    Work email <span className="text-gold">*</span>
                  </Label>
                  <Input
                    id="dp-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    autoComplete="email"
                    className="bg-paper border-line text-ink"
                    data-testid="design-partner-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="dp-company" className="text-xs uppercase tracking-wider text-ink/60">
                    Company
                  </Label>
                  <Input
                    id="dp-company"
                    value={form.company}
                    onChange={update("company")}
                    autoComplete="organization"
                    className="bg-paper border-line text-ink"
                    data-testid="design-partner-company"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dp-target" className="text-xs uppercase tracking-wider text-ink/60">
                    Target URL or repo
                  </Label>
                  <Input
                    id="dp-target"
                    value={form.target_url}
                    onChange={update("target_url")}
                    placeholder="https://app.example.com"
                    className="bg-paper border-line text-ink"
                    data-testid="design-partner-target"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-ink/60">
                  What do you need scanned?
                </Label>
                <Select value={form.use_case} onValueChange={update("use_case")}>
                  <SelectTrigger
                    className="bg-paper border-line text-ink"
                    data-testid="design-partner-usecase"
                  >
                    <SelectValue placeholder="Pick one" />
                  </SelectTrigger>
                  <SelectContent className="bg-panel border-line text-ink">
                    {USE_CASES.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        {u.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dp-note" className="text-xs uppercase tracking-wider text-ink/60">
                  Anything else?
                </Label>
                <Textarea
                  id="dp-note"
                  value={form.note}
                  onChange={update("note")}
                  rows={3}
                  placeholder="Compliance deadline, stack details, who'll be the security lead, etc."
                  className="bg-paper border-line text-ink resize-none"
                  data-testid="design-partner-note"
                />
              </div>

              <CaptchaWidget onToken={setCaptchaToken} />
            </div>

            <DialogFooter className="mt-6 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-line bg-transparent text-ink hover:bg-raised"
                data-testid="design-partner-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gold text-paper hover:bg-gold2 font-semibold"
                data-testid="design-partner-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" /> Sending…
                  </>
                ) : (
                  "Send to Security"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
